import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../../styles/hub-v2.css';
import '../../styles/hub-notes.css';
import '../../styles/prepare-up-home.css';
import '../../styles/prepare-up-hub.css';
import APIService from '../../services/APIService';
import HubNodeEditor from './HubNodeEditor';
import HubNotesEditor from './HubNotesEditor';
import PackReader from './PackReader';
import PracticeChooser from './PracticeChooser';
import { parseNotes, renderNotes, ensureStyle, docToMarkdown } from './notesDoc';
import { useDialog, useToast } from './ui/dialogs';
import { IconArrowRight, IconBook, IconCards, IconChevDown, IconChevRight, IconCode, IconList, IconPlus, IconSearch, IconTarget } from './icons';
import { buildPaths, CURATED_AREAS, indexTree } from './paths';
import { Bar, HomeSearch, ThemeToggle, buildSearchEntries, loadGoal, resolveGoal } from './HomeDashboard';

/*
 * KnowledgeHubV2 — Prepare Up's knowledge workspace.
 *
 *   Hub home   My Skills (from the learner's goal) + All skills (search, filter
 *              by career path) · tabs for My pages and Library
 *   Skill      a compact topic list (topics → concepts, progressive disclosure)
 *              beside the selected topic's concept index
 *   Concept    breadcrumb, Notes / Questions, "Practice this" (MCQ · Theory ·
 *              Flashcards · Case Study → the existing practice screens)
 *
 * The hub tree is skill (root) → topic (subtopic) → concept → node. Every page
 * renders from MARKDOWN (notesDoc.js); a learner's own pages store theirs and
 * shared pages are edited through a copy-on-write fork. URLs stay
 * content-addressed (#/hub2/ai.rag.rm).
 */

const MYHUB = { id: 'myhub', name: 'My pages' };
const IMPORTANCE = { must_know: ['Must know', 'must'], core: ['Core', ''], advanced: ['Advanced', 'adv'] };
const pct = (v) => `${Math.round((Number(v) || 0) * 100)}%`;

const ago = (days) => {
  if (days == null) return '';
  const d = Math.floor(days);
  if (d <= 0) return 'today';
  if (d === 1) return 'yesterday';
  if (d < 7) return `${d}d ago`;
  if (d < 60) return `${Math.floor(d / 7)}w ago`;
  return `${Math.floor(d / 30)}mo ago`;
};

const Mastery = ({ value }) => (
  <span className="kh-mast" title={value == null ? 'Not practised yet' : `${pct(value)} held`}>
    {value == null ? <em>Not practised</em> : <><Bar value={value} label={`${pct(value)} held`} /><b>{pct(value)}</b></>}
  </span>
);

const revisionLine = (st) => {
  if (!st || !st.attempts) return 'Not practised yet';
  return `Practised ${st.attempts}×${st.last_seen_days == null ? '' : ` · ${ago(st.last_seen_days)}`}`;
};

const flattenMemory = (tree) => {
  const map = {};
  const walk = (n) => {
    map[n.id] = { mastery: n.attempts > 0 ? n.mastery : null, attempts: n.attempts || 0,
                  last_seen_days: n.last_seen_days, status: n.status };
    (n.children || []).forEach(walk);
  };
  (tree || []).forEach(walk);
  return map;
};

/** Every page becomes markdown first, then the notes layout. */
const useNotes = (doc, fallbackTitle) => useMemo(() => {
  const md = (doc && doc.markdown) || docToMarkdown(doc, fallbackTitle);
  const parsed = parseNotes(md);
  const saved = doc && doc.style && Object.keys(doc.style.blocks || {}).length ? doc.style : null;
  return { parsed, style: saved || ensureStyle(parsed, null) };
}, [doc, fallbackTitle]);

const Crumbs = ({ items }) => (
  <nav className="kh-crumbs" aria-label="Breadcrumb">
    {items.map((c, i) => (
      <React.Fragment key={`${c.label}-${i}`}>
        {i > 0 && <IconChevRight size={12} />}
        {c.onClick && i < items.length - 1
          ? <button type="button" onClick={c.onClick}>{c.label}</button>
          : <span aria-current={i === items.length - 1 ? 'page' : undefined}>{c.label}</span>}
      </React.Fragment>
    ))}
  </nav>
);

const PRACTICE_RAIL = [
  { id: 'mcq', label: 'MCQ', note: 'Test your understanding', Icon: IconList },
  { id: 'theory', label: 'Theory', note: 'Explain in your own words', Icon: IconBook },
  { id: 'flashcards', label: 'Flashcards', note: 'Quick revision', Icon: IconCards },
  { id: 'case', label: 'Case Study', note: 'Apply it to a scenario', Icon: IconTarget },
];

/* ── a concept's own page ───────────────────────────────────────────────── */
const HubConceptPage = ({ concept, topic, sub, mem, siblings, isUser, myNodes, crumbs, onEdit, onNavigate, onPracticeWith, onNewPage }) => {
  const [data, setData] = useState(null);
  const [items, setItems] = useState(null);     // questions the concept owns itself
  const [nodes, setNodes] = useState(null);     // the last layer: child nodes + their questions
  const [forking, setForking] = useState(false);
  const [tab, setTab] = useState('notes');
  const [moreOpen, setMoreOpen] = useState(false);
  const toast = useToast();

  const byDepth = (a, b) => (a.depth || 9) - (b.depth || 9);

  useEffect(() => {
    let alive = true;
    setData(null); setItems(null); setNodes(null); setTab('notes');
    APIService.getHubLearn(concept.id)
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData({}); });
    if (isUser) {
      APIService.getHubQuestions([concept.id])
        .then((d) => { if (alive) { setItems((d.items || []).sort(byDepth)); setNodes([]); } })
        .catch(() => { if (alive) { setItems([]); setNodes([]); } });
    } else {
      APIService.getHubNode(concept.id).then(async (nd) => {
        const own = (nd.items || []).filter((i) => i.type === 'interview_q').sort(byDepth);
        if (alive) setItems(own);
        // Each child node is a section of this page — its own questions, its own
        // memory. This is the layer the engine and the memory map key on.
        const kids = await Promise.all((nd.children || []).map((c) =>
          APIService.getHubNode(c.id)
            .then((x) => ({
              id: c.id,
              name: c.name,
              items: (x.items || []).filter((i) => i.type === 'interview_q').sort(byDepth),
            }))
            .catch(() => ({ id: c.id, name: c.name, items: [] }))));
        if (alive) setNodes(kids);
      }).catch(() => { if (alive) { setItems([]); setNodes([]); } });
    }
    return () => { alive = false; };
  }, [concept.id, isUser]);

  const st = mem[concept.id];
  const imp = IMPORTANCE[(concept.meta || {}).importance];
  const doc = data?.doc;
  const { parsed, style } = useNotes(doc, null);
  const idx = siblings.findIndex((s) => s.id === concept.id);
  const prev = siblings[idx - 1];
  const next = siblings[idx + 1];
  const forked = !isUser && (myNodes || []).find((n) => n.forked_from === concept.id);
  const questionCount = (items || []).length + (nodes || []).reduce((n, x) => n + x.items.length, 0);
  const subject = [{ id: concept.id, name: concept.name, kind: 'concept' }];

  const editThis = async () => {
    setMoreOpen(false);
    if (isUser) { onEdit(concept.id); return; }
    // Copy-on-write, once: if this page was already forked, edit that copy.
    if (forked) { onEdit(forked.concept_id); return; }
    setForking(true);
    try {
      const { node } = await APIService.forkHubNode(concept.id);
      onEdit(null, node);
    } catch (e) { toast.error(e.message); } finally { setForking(false); }
  };

  return (
    <div className="kh-concept">
      <div className="kh-concept-main">
        <Crumbs items={crumbs} />
        <header className="kh-concept-head">
          <div className="kh-concept-title">
            <h1>{concept.name}</h1>
          </div>
          <div className="kh-actions">
            <button type="button" className="pu-btn pu-btn-quiet" onClick={editThis} disabled={forking}
              title={isUser ? 'Edit this page' : 'Edits your own copy — the shared page stays untouched'}>
              {forking ? 'Copying…' : isUser || forked ? 'Edit' : 'Edit my copy'}
            </button>
            <button type="button" className="pu-btn pu-btn-primary" onClick={() => onPracticeWith(subject, concept.name)}>
              <IconPlus size={14} /> Practice this
            </button>
            <div className="kh-more">
              <button type="button" className="kh-icon-btn" aria-haspopup="menu" aria-expanded={moreOpen}
                aria-label="More actions" onClick={() => setMoreOpen((o) => !o)}>⋯</button>
              {moreOpen && (
                <div className="kh-menu" role="menu" onMouseLeave={() => setMoreOpen(false)}>
                  <button type="button" role="menuitem" onClick={editThis}>{isUser || forked ? 'Edit page' : 'Make my own copy'}</button>
                  <button type="button" role="menuitem" onClick={() => { setMoreOpen(false); onNewPage(); }}>New page</button>
                </div>
              )}
            </div>
          </div>
        </header>
        {(parsed.lead || doc?.intro) && <p className="kh-lead">{parsed.lead || doc.intro}</p>}
        <p className="kh-meta">
          <span className="kh-source">{isUser ? 'My notes' : forked ? 'Shared · you have a copy' : 'Shared notes'}</span>
          {imp && <span className="kh-source">{imp[0]}</span>}
          <span>{revisionLine(st)}</span>
          {st?.mastery != null && <Mastery value={st.mastery} />}
        </p>

        <div className="kh-tabs" role="tablist" aria-label="Page sections">
          <button type="button" role="tab" aria-selected={tab === 'notes'} className={tab === 'notes' ? 'on' : ''} onClick={() => setTab('notes')}>Notes</button>
          {questionCount > 0 && (
            <button type="button" role="tab" aria-selected={tab === 'questions'} className={tab === 'questions' ? 'on' : ''} onClick={() => setTab('questions')}>
              Questions ({questionCount})
            </button>
          )}
        </div>

        <div className="kh-reading">
          {tab === 'notes' ? (
            data === null ? <div className="hb-skel"><i /><i /><i /></div> : (
              <>
                {data?.inherited_from && (
                  <div className="tip">Showing notes from <b>{data.inherited_from}</b> — this concept's own page is coming.</div>
                )}
                {parsed.concepts.length ? renderNotes(parsed, style)
                  : <div className="hb-empty">No notes on this page yet. {isUser ? 'Use Edit to write them.' : 'Use “Edit my copy” to write your own.'}</div>}
              </>
            )
          ) : (
            <>
              {(items || []).length > 0 && (
                <section className="kh-qs">
                  <h3>What you'll be asked</h3>
                  <ol>{items.slice(0, 12).map((q) => <li key={q.id}>{q.payload?.question}</li>)}</ol>
                  {items.length > 12 && <p className="kh-more-line">+ {items.length - 12} more in practice</p>}
                </section>
              )}
              {(nodes || []).filter((n) => n.items.length).map((n) => {
                const nst = mem[n.id];
                return (
                  <section className="kh-qs" id={n.id} key={n.id}>
                    <header>
                      <h3>{n.name}</h3>
                      <span className="kh-qs-side">
                        <Mastery value={nst?.mastery} />
                        <button type="button" className="pu-link" onClick={() => onPracticeWith([{ id: n.id, name: n.name, kind: 'node' }], n.name)}>
                          Practice <IconArrowRight size={12} />
                        </button>
                      </span>
                    </header>
                    <ol>{n.items.slice(0, 8).map((q) => <li key={q.id}>{q.payload?.question}</li>)}</ol>
                    {n.items.length > 8 && <p className="kh-more-line">+ {n.items.length - 8} more in practice</p>}
                  </section>
                );
              })}
            </>
          )}
        </div>

        {(prev || next) && (
          <nav className="kh-pager" aria-label="Reading order">
            {prev ? (
              <button type="button" onClick={() => onNavigate({ topic: topic?.id, sub: sub?.id, concept: prev.id })}>
                <small>Previous</small><strong>← {prev.name}</strong>
              </button>
            ) : <span />}
            {next ? (
              <button type="button" className="next" onClick={() => onNavigate({ topic: topic?.id, sub: sub?.id, concept: next.id })}>
                <small>Next</small><strong>{next.name} →</strong>
              </button>
            ) : <span />}
          </nav>
        )}
      </div>

      <aside className="kh-rail" aria-label="Practice this topic">
        <div className="pu-panel kh-rail-card">
          <h2>Practice this topic</h2>
          <p className="pu-meta">Turn this page into practice questions.</p>
          <ul className="kh-rail-modes">
            {PRACTICE_RAIL.map(({ id, label, note, Icon }) => (
              <li key={id}>
                <button type="button" className={`m-${id}`} onClick={() => onPracticeWith(subject, concept.name, id)}>
                  <span className="kh-mode-icon" aria-hidden="true"><Icon size={16} /></span>
                  <span><b>{label}</b><small>{note}</small></span>
                  <IconChevRight size={14} />
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="pu-panel kh-rail-card">
          <h2>Quick actions</h2>
          <ul className="kh-quick">
            <li><button type="button" onClick={editThis}>{isUser || forked ? 'Edit this page' : 'Edit my copy'}</button></li>
            <li><button type="button" onClick={onNewPage}>Add a new page</button></li>
          </ul>
        </div>
      </aside>
    </div>
  );
};

/* ── the hub ────────────────────────────────────────────────────────────── */
const KnowledgeHubV2 = ({
  openNodeId, onOpenNode, onOpenDsa, onPractice, onStartPractice,
  profile, onEditProfile, theme, onToggleTheme, onOpenPath,
}) => {
  const [tree, setTree] = useState(null);
  const [mem, setMem] = useState({});
  const [memTree, setMemTree] = useState(null);
  const [summaries, setSummaries] = useState({});
  const [myNodes, setMyNodes] = useState(null);
  const [editing, setEditing] = useState(null);        // AI draft / import editor: 'new' | node
  const [notesEdit, setNotesEdit] = useState(null);    // page editor: {node?, parentId?}
  const [libPacks, setLibPacks] = useState(null);
  const [discover, setDiscover] = useState(null);
  const [openPack, setOpenPack] = useState(null);
  const [topicQuery, setTopicQuery] = useState('');
  const [skillQuery, setSkillQuery] = useState('');
  const [career, setCareer] = useState('all');
  const [expanded, setExpanded] = useState(null);      // topic open in the skill nav
  const [practice, setPractice] = useState(null);      // { subjects, title, mode, coding }
  const [headMenu, setHeadMenu] = useState(false);
  const { confirm, prompt } = useDialog();
  const toast = useToast();
  // The hub opens on its home; deep links (#/hub2/<id>) open their page.
  const [sel, setSel] = useState({ topic: 'home' });

  const refreshMine = useCallback(() => {
    APIService.listUserHubNodes().then((d) => setMyNodes(d.nodes || [])).catch(() => setMyNodes([]));
  }, []);

  const myTopics = useMemo(() => (myNodes || []).filter((n) => n.kind === 'topic'), [myNodes]);
  const mySubs = useMemo(() => (myNodes || []).filter((n) => n.kind === 'subtopic'), [myNodes]);
  const myPages = useMemo(
    () => (myNodes || []).filter((n) => n.kind !== 'topic' && n.kind !== 'subtopic'),
    [myNodes],
  );
  const myTopicTree = useMemo(() => myTopics.map((t) => ({
    id: t.id, name: t.name,
    subs: mySubs.filter((s) => s.parent_id === t.id).map((s) => ({ id: s.id, name: s.name })),
  })), [myTopics, mySubs]);
  const [myTopicSel, setMyTopicSel] = useState('all');

  const createMyTopic = useCallback(async (name, kind = 'topic', parentId = null) => {
    const { node } = await APIService.createUserHubNode({ name, kind, parent_id: parentId });
    refreshMine();
    return node;
  }, [refreshMine]);

  const renameMyTopic = useCallback(async (t) => {
    const name = await prompt({ title: 'Rename topic', defaultValue: t.name, confirmLabel: 'Rename',
      validate: (v) => (v.trim() ? '' : 'Give it a name') });
    if (name == null || name === t.name) return;
    try { await APIService.updateUserHubNode(t.id, { name }); refreshMine(); }
    catch (e) { toast.error(e.message); }
  }, [refreshMine, prompt, toast]);

  const deleteMyTopic = useCallback(async (t) => {
    const ok = await confirm({
      title: t.kind === 'subtopic' ? `Delete subtopic "${t.name}"?` : `Delete topic "${t.name}"?`,
      message: t.kind === 'subtopic'
        ? 'Its pages move up to the topic — nothing is lost.'
        : 'Its subtopics go too; every page moves to Unfiled — nothing is lost.',
      confirmLabel: 'Delete', danger: true,
    });
    if (!ok) return;
    try {
      await APIService.deleteUserHubNode(t.id);
      setMyTopicSel((s) => (s === t.id ? 'all' : s));
      refreshMine();
      toast.success(`Deleted "${t.name}"`);
    } catch (e) { toast.error(e.message); }
  }, [refreshMine, confirm, toast]);

  const refreshLibrary = useCallback(() => {
    APIService.myPackSubscriptions().then((d) => setLibPacks(d.packs || [])).catch(() => setLibPacks([]));
    APIService.discoverPacks().then((d) => setDiscover(d.packs || [])).catch(() => setDiscover([]));
  }, []);

  useEffect(() => {
    APIService.getHubTree().then((d) => setTree(d.tree || [])).catch(() => setTree([]));
    APIService.getMemoryTree().then((m) => { setMem(flattenMemory(m.tree)); setMemTree(m.tree || []); }).catch(() => setMemTree([]));
    refreshMine();
  }, [refreshMine]);

  const topics = useMemo(() => tree || [], [tree]);
  const isUserNode = String(openNodeId || '').startsWith('u.');
  const byId = useMemo(() => indexTree(topics), [topics]);
  const paths = useMemo(() => (tree ? buildPaths(tree) : null), [tree]);

  const located = useMemo(() => {
    if (!openNodeId) return null;
    if (isUserNode) {
      const mine = myNodes || [];
      const row = mine.find((n) => n.concept_id === openNodeId);
      const siblings = mine.filter((n) => n.kind !== 'topic' && n.kind !== 'subtopic')
        .map((n) => ({ id: n.concept_id, name: n.name, kind: n.kind, meta: {} }));
      return {
        topic: MYHUB, sub: { id: 'myhub', name: 'My pages' },
        concept: { id: openNodeId, name: row?.name || 'My page', kind: row?.kind || 'concept', meta: {} },
        siblings, uhId: row?.id,
      };
    }
    if (!topics.length) return null;
    for (const t of topics) {
      for (const s of t.children || []) {
        const kids = (s.children || []).length ? s.children : [s];
        const c = kids.find((k) => k.id === openNodeId);
        if (c) return { topic: t, sub: s, concept: c, siblings: kids };
      }
    }
    return null;
  }, [openNodeId, topics, isUserNode, myNodes]);

  const view = located ? 'concept'
    : sel.topic === 'home' ? 'home'
      : sel.topic === 'myhub' ? 'myhub'
        : sel.topic === 'library' ? 'library'
          : 'skill';
  const onLibrary = view === 'library';
  const topic = located?.topic && located.topic.id !== 'myhub' ? located.topic
    : topics.find((t) => t.id === sel.topic) || null;
  const subs = useMemo(() => (topic?.children || []).filter((s) => (s.children || []).length || s.item_count), [topic]);
  const sub = (located?.sub && located.sub.id !== 'myhub' ? located.sub : null)
    || subs.find((s) => s.id === sel.sub) || null;
  const concepts = useMemo(() => (sub ? ((sub.children || []).length ? sub.children : [sub]) : []), [sub]);

  const pick = (patch) => {
    const nextSel = { topic: topic?.id, sub: sub?.id, ...patch };
    setSel(nextSel);
    setEditing(null); setNotesEdit(null); setOpenPack(null); setHeadMenu(false);
    if (nextSel.sub) setExpanded(nextSel.sub);
    onOpenNode?.(null);
  };
  const goHome = () => pick({ topic: 'home', sub: undefined });
  // Open a "skill" by id — a hub root, or a featured sub-area (e.g. ai.rag).
  const openSkill = (id) => {
    if (topics.some((t) => t.id === id)) { pick({ topic: id, sub: undefined }); return; }
    const root = topics.find((t) => String(id).startsWith(`${t.id}.`));
    if (root) pick({ topic: root.id, sub: id });
  };

  useEffect(() => { if (sub?.id) setExpanded(sub.id); }, [sub?.id]);

  useEffect(() => {
    if (onLibrary && libPacks === null) refreshLibrary();
  }, [onLibrary, libPacks, refreshLibrary]);

  useEffect(() => {
    const missing = concepts.filter((c) => !(c.id in summaries)).map((c) => c.id);
    if (!missing.length || located || view !== 'skill') return;
    APIService.getHubLearnSummary(missing)
      .then((d) => setSummaries((s) => {
        const nxt = { ...s };
        (d.summaries || []).forEach((x) => { nxt[x.node_id] = x; });
        missing.forEach((id) => { if (!(id in nxt)) nxt[id] = {}; });
        return nxt;
      }))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [concepts, located, view]);

  /* the page editor — for a learner's own page (shared pages fork first) */
  const openNotesEditor = useCallback(async (conceptId, freshNode) => {
    if (freshNode) { refreshMine(); setNotesEdit({ node: freshNode }); onOpenNode?.(null); return; }
    try {
      let row = (myNodes || []).find((n) => n.concept_id === conceptId);
      if (!row) {
        const d = await APIService.listUserHubNodes();
        setMyNodes(d.nodes || []);
        row = (d.nodes || []).find((n) => n.concept_id === conceptId);
      }
      if (!row) { toast.error('This page was not found in your hub.'); return; }
      const { node } = await APIService.getUserHubNode(row.id);
      setNotesEdit({ node });
      onOpenNode?.(null);
    } catch (e) { toast.error(e.message); }
  }, [myNodes, onOpenNode, refreshMine, toast]);

  const saveNotes = useCallback(async ({ name, markdown, style, intro }) => {
    const node = notesEdit?.node;
    const doc = { intro, tabs: node?.doc?.tabs || [], markdown, style };
    if (node?.id) {
      await APIService.updateUserHubNode(node.id, { name, doc });
      setNotesEdit((s) => ({ ...s, node: { ...s.node, name, doc } }));
    } else {
      const { node: created } = await APIService.createUserHubNode({
        name, doc, parent_id: notesEdit?.parentId || null,
      });
      setNotesEdit({ node: created });
    }
    refreshMine();
  }, [notesEdit, refreshMine]);

  const deleteNotesPage = useCallback(async () => {
    const node = notesEdit?.node;
    if (!node?.id) { setNotesEdit(null); return; }
    const ok = await confirm({
      title: `Delete "${node.name}"?`,
      message: 'Its questions and practice history stay in your memory log, but the page is gone for good.',
      confirmLabel: 'Delete page', danger: true,
    });
    if (!ok) return;
    try {
      await APIService.deleteUserHubNode(node.id);
      setNotesEdit(null); refreshMine();
      toast.success('Page deleted');
    } catch (e) { toast.error(e.message); }
  }, [notesEdit, confirm, refreshMine, toast]);

  const newPage = (parentId = null) => { setNotesEdit({ parentId }); setHeadMenu(false); onOpenNode?.(null); };
  const draftWithAi = () => { setEditing('new'); setHeadMenu(false); onOpenNode?.(null); };

  // "Practice this" → choose a mode → the existing practice screens.
  const practiceWith = (subjects, title, mode) => {
    const coding = subjects.some((s) => s.id === 'dsa' || String(s.id).startsWith('dsa.'));
    setPractice({ subjects, title, mode: mode || 'mcq', coding });
  };
  const startPractice = (opts) => {
    setPractice(null);
    if (onStartPractice) onStartPractice(opts);
    else onPractice?.(opts.subjects);
  };

  // The top search (same as Home) also finds this learner's own pages.
  const searchEntries = useMemo(() => {
    const base = buildSearchEntries(tree, paths ? [...paths.roles, ...paths.areas] : []);
    (myPages || []).forEach((p) => base.push({ kind: 'node', id: p.concept_id, name: p.name, trail: 'My pages', depth: 1 }));
    return base;
  }, [tree, paths, myPages]);

  /* skills: hub roots + featured sub-areas; careers are the role paths */
  const careers = useMemo(() => paths?.roles || [], [paths]);
  const allSkills = useMemo(() => {
    if (!paths) return [];
    const ids = career === 'all' ? paths.areas.map((a) => a.id) : (careers.find((c) => c.id === career)?.rootIds || []);
    return ids.map((id) => byId[id]).filter(Boolean);
  }, [paths, career, careers, byId]);
  const goal = useMemo(() => (paths ? resolveGoal([...paths.roles, ...paths.areas], loadGoal(), null, memTree || []) : null), [paths, memTree]);
  const mySkills = useMemo(() => {
    if (!goal) return [];
    const ids = goal.rootIds.slice(0, 4);
    if (ids.length < 4) {
      // top up with the skills practised most
      topics.filter((t) => !ids.includes(t.id) && mem[t.id]?.attempts)
        .sort((a, b) => (mem[b.id].attempts || 0) - (mem[a.id].attempts || 0))
        .forEach((t) => { if (ids.length < 4) ids.push(t.id); });
    }
    return ids.map((id) => byId[id]).filter(Boolean);
  }, [goal, topics, mem, byId]);
  const topicCount = (n) => (n.children || []).length;
  const blurb = (n) => CURATED_AREAS[n.id]?.blurb || '';

  const skillCard = (n, compact) => {
    const st = mem[n.id];
    return (
      <button key={n.id} type="button" className={`kh-skill${compact ? ' compact' : ''}`} onClick={() => openSkill(n.id)}>
        <span className="kh-skill-mark" aria-hidden="true">{n.name.slice(0, 1)}</span>
        <span className="kh-skill-body">
          <b>{n.name}</b>
          <small>{topicCount(n)} topics{st?.attempts ? ` · practised ${ago(st.last_seen_days)}` : ''}</small>
          {compact && <span className="kh-skill-bar"><Bar value={st?.mastery || 0} label={st?.mastery == null ? 'Not practised' : `${pct(st.mastery)} held`} /><em>{st?.mastery == null ? '—' : pct(st.mastery)}</em></span>}
          {!compact && blurb(n) && <span className="kh-skill-blurb">{blurb(n)}</span>}
        </span>
        <IconChevRight size={14} />
      </button>
    );
  };

  /* ── loading ─────────────────────────────────────────────────────────── */
  const displayName = profile?.display_name || profile?.name || '';
  const topbar = (
    <header className="pu-topbar">
      <HomeSearch entries={searchEntries}
        onPick={(e) => (e.kind === 'track' ? onOpenPath?.(e.id) : onOpenNode?.(e.id))} />
      <div className="pu-topbar-end">
        {onToggleTheme && <ThemeToggle theme={theme} onToggle={onToggleTheme} />}
        <button type="button" className="pu-avatar" onClick={onEditProfile}
          aria-label={displayName ? `${displayName} — profile` : 'Profile'}>
          {profile?.avatar ? <img src={profile.avatar} alt="" /> : <span>{(displayName || 'U').slice(0, 1).toUpperCase()}</span>}
        </button>
      </div>
    </header>
  );
  const shell = (content) => (
    <div className="pu-home pu-hub">
      <div className="pu-page">
        {topbar}
        <div className="hb kh">{content}</div>
      </div>
      {practice && (
        <PracticeChooser title={practice.title} subjects={practice.subjects} initialMode={practice.mode}
          coding={practice.coding} onOpenCoding={() => { setPractice(null); onOpenDsa?.(); }}
          onStart={startPractice} onClose={() => setPractice(null)} />
      )}
    </div>
  );

  if (tree === null) return shell(<div className="hb-skel"><i /><i /><i /></div>);

  const hubHeader = (
    <section className="kh-head">
      <div>
        <h1>Knowledge Hub</h1>
        <p>Your personalized learning library. Create, edit and explore knowledge connected to your skills and goals.</p>
      </div>
      <div className="kh-actions">
        <button type="button" className="pu-btn pu-btn-primary" onClick={() => newPage(null)}><IconPlus size={14} /> Add page</button>
        <div className="kh-more">
          <button type="button" className="kh-icon-btn" aria-haspopup="menu" aria-expanded={headMenu}
            aria-label="More actions" onClick={() => setHeadMenu((o) => !o)}>⋯</button>
          {headMenu && (
            <div className="kh-menu" role="menu" onMouseLeave={() => setHeadMenu(false)}>
              <button type="button" role="menuitem" onClick={draftWithAi}>Draft a page with AI / import</button>
              <button type="button" role="menuitem" onClick={() => pick({ topic: 'myhub', sub: undefined })}>Organise my pages</button>
              <button type="button" role="menuitem" onClick={() => pick({ topic: 'library', sub: undefined })}>Browse the Library</button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
  const hubTabs = (
    <div className="kh-tabs" role="tablist" aria-label="Knowledge Hub">
      {[['home', 'My Hub'], ['myhub', `My pages${myPages.length ? ` (${myPages.length})` : ''}`], ['library', 'Library']].map(([id, label]) => (
        <button key={id} type="button" role="tab" aria-selected={view === id} className={view === id ? 'on' : ''}
          onClick={() => pick({ topic: id, sub: undefined })}>{label}</button>
      ))}
    </div>
  );

  /* ── editors and packs take the whole content area ───────────────────── */
  if (notesEdit) {
    const node = notesEdit.node;
    return shell(
      <>
        <Crumbs items={[{ label: 'Knowledge Hub', onClick: goHome }, { label: 'My pages', onClick: () => pick({ topic: 'myhub', sub: undefined }) }, { label: node?.name || 'New page' }]} />
        <div className="kh-editor">
          <HubNotesEditor
            key={node?.id || 'new'}
            title={node?.name || ''}
            markdown={node?.doc?.markdown || (node ? docToMarkdown(node.doc, node.name) : '')}
            style={node?.doc?.style || null}
            forkedFrom={node?.forked_from || null}
            onSave={saveNotes}
            onDelete={node?.id ? deleteNotesPage : null}
            onCancel={() => {
              const openId = notesEdit.node?.concept_id;
              setNotesEdit(null);
              if (openId) onOpenNode?.(openId);
            }}
          />
        </div>
      </>,
    );
  }
  if (editing) {
    return shell(
      <>
        <Crumbs items={[{ label: 'Knowledge Hub', onClick: goHome }, { label: 'Draft a page' }]} />
        <div className="hb-page kh-plain">
          <HubNodeEditor node={editing === 'new' ? null : editing}
            topics={myTopicTree}
            initialParentId={myTopicSel !== 'all' && myTopicSel !== 'unfiled' ? myTopicSel : null}
            onNewTopic={createMyTopic}
            onDeleted={() => { setEditing(null); refreshMine(); }}
            onCancel={() => setEditing(null)}
            onSaved={(saved) => { setEditing(null); refreshMine(); onOpenNode?.(saved.concept_id); }} />
        </div>
      </>,
    );
  }
  if (openPack) {
    return shell(
      <>
        <Crumbs items={[{ label: 'Knowledge Hub', onClick: goHome }, { label: 'Library', onClick: () => { setOpenPack(null); refreshLibrary(); } }, { label: 'Pack' }]} />
        <div className="hb-page kh-plain">
          <PackReader packId={openPack} onPractice={(subjects) => practiceWith(subjects, 'this pack')}
            onBack={() => { setOpenPack(null); refreshLibrary(); }} />
        </div>
      </>,
    );
  }

  /* ── hub home / My pages / Library ───────────────────────────────────── */
  if (view === 'home' || view === 'myhub' || view === 'library') {
    const term = skillQuery.trim().toLowerCase();
    const shownSkills = allSkills.filter((n) => !term || n.name.toLowerCase().includes(term));
    let panel;
    if (view === 'home') {
      panel = (
        <>
          {mySkills.length > 0 && (
            <section className="kh-sec" aria-labelledby="kh-myskills">
              <div className="kh-sec-head">
                <h2 id="kh-myskills">My Skills</h2>
                {goal && <span className="pu-meta">From your goal: {goal.title}</span>}
              </div>
              <div className="kh-skills compact">{mySkills.map((n) => skillCard(n, true))}</div>
            </section>
          )}
          <section className="kh-sec" aria-labelledby="kh-allskills">
            <div className="kh-sec-head">
              <h2 id="kh-allskills">All skills</h2>
              <div className="kh-filters">
                <label className="kh-search">
                  <IconSearch size={14} />
                  <input type="search" value={skillQuery} onChange={(e) => setSkillQuery(e.target.value)}
                    placeholder="Search skills…" aria-label="Search skills" />
                </label>
                <label className="kh-select">
                  <span className="pu-sr">Filter by career path</span>
                  <select value={career} onChange={(e) => setCareer(e.target.value)} aria-label="Filter by career path">
                    <option value="all">All skills</option>
                    {careers.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                  </select>
                  <IconChevDown size={14} />
                </label>
              </div>
            </div>
            {career !== 'all' && (
              <p className="pu-meta kh-career-note">
                Skills for {careers.find((c) => c.id === career)?.title} — {careers.find((c) => c.id === career)?.blurb}
              </p>
            )}
            {shownSkills.length ? <div className="kh-skills">{shownSkills.map((n) => skillCard(n, false))}</div>
              : <p className="hb-empty">No skill matches “{skillQuery}”.</p>}
            {myTopics.length > 0 && career === 'all' && (
              <>
                <h3 className="kh-sub-h">Your own topics</h3>
                <div className="kh-skills">
                  {myTopics.filter((t) => !term || t.name.toLowerCase().includes(term)).map((t) => (
                    <button key={t.id} type="button" className="kh-skill" onClick={() => { setMyTopicSel(t.id); pick({ topic: 'myhub', sub: undefined }); }}>
                      <span className="kh-skill-mark mine" aria-hidden="true">{t.name.slice(0, 1)}</span>
                      <span className="kh-skill-body"><b>{t.name}</b><small>My notes</small></span>
                      <IconChevRight size={14} />
                    </button>
                  ))}
                </div>
              </>
            )}
          </section>
        </>
      );
    } else if (view === 'myhub') {
      panel = (
        <MyHubPanel
          myNodes={myNodes} myTopics={myTopics} mySubs={mySubs} myPages={myPages}
          sel={myTopicSel} onSelect={setMyTopicSel} mem={mem}
          onOpen={(id) => onOpenNode?.(id)}
          onNewPage={(parentId) => newPage(parentId || null)}
          onDraftWithAi={draftWithAi}
          onCreateTopic={createMyTopic} onRenameTopic={renameMyTopic} onDeleteTopic={deleteMyTopic}
          prompt={prompt} toast={toast} />
      );
    } else {
      panel = (
        <div className="kh-plain">
          <p className="pu-meta kh-tab-note">Study material published by creators. Packs you subscribe to read and practise like the rest of the hub.</p>
          {libPacks === null ? <div className="hb-skel"><i /><i /><i /></div>
            : libPacks.length === 0 ? <div className="hb-empty">You haven't subscribed to any packs yet — pick one below and it becomes part of your hub.</div>
              : (
                <div className="hb-index">
                  {libPacks.map((p, i) => (
                    <div key={p.id} className="hb-row" role="group">
                      <span className="hb-row-num">{String(i + 1).padStart(2, '0')}</span>
                      <button type="button" className="hb-row-main kh-row-btn" onClick={() => setOpenPack(p.id)}>
                        <h3>{p.title}</h3>
                        <p>{p.tagline || `${p.page_count} page${p.page_count === 1 ? '' : 's'} by ${p.creator_name || 'a creator'}`}</p>
                      </button>
                      <div className="hb-row-side">
                        <span className="cnt">{p.page_count} page{p.page_count === 1 ? '' : 's'}</span>
                        <button className="hb-btn" type="button" onClick={async () => {
                          try { await APIService.unsubscribePack(p.id); refreshLibrary(); toast.info(`Removed "${p.title}"`); }
                          catch (e) { toast.error(e.message); }
                        }}>Unsubscribe</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          {(discover || []).filter((p) => !p.subscribed).length > 0 && (
            <>
              <h3 className="kh-sub-h">Discover packs</h3>
              <div className="hb-index">
                {(discover || []).filter((p) => !p.subscribed).map((p, i) => (
                  <div key={p.id} className="hb-row" role="group">
                    <span className="hb-row-num">{String(i + 1).padStart(2, '0')}</span>
                    <div className="hb-row-main">
                      <h3>{p.title}</h3>
                      <p>{p.tagline || `${p.page_count} page${p.page_count === 1 ? '' : 's'} by ${p.creator_name || 'a creator'}`}</p>
                    </div>
                    <div className="hb-row-side">
                      <span className="cnt">{p.subscriber_count} subscriber{p.subscriber_count === 1 ? '' : 's'}</span>
                      <button className="hb-btn go" type="button" onClick={async () => {
                        try { await APIService.subscribePack(p.id); refreshLibrary(); toast.success(`Added "${p.title}"`); }
                        catch (e) { toast.error(e.message); }
                      }}>+ Subscribe</button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      );
    }
    return shell(<>{hubHeader}{hubTabs}{panel}</>);
  }

  /* ── a user's own page (no skill tree around it) ─────────────────────── */
  if (located && isUserNode) {
    return shell(
      <HubConceptPage
        concept={located.concept} topic={located.topic} sub={located.sub}
        mem={mem} siblings={located.siblings} isUser myNodes={myNodes}
        crumbs={[{ label: 'Knowledge Hub', onClick: goHome }, { label: 'My pages', onClick: () => pick({ topic: 'myhub', sub: undefined }) }, { label: located.concept.name }]}
        onEdit={openNotesEditor} onPracticeWith={practiceWith} onNewPage={() => newPage(null)}
        onNavigate={({ concept: c }) => { if (c) onOpenNode?.(c); }} />,
    );
  }

  /* ── skill: topic list beside the topic / concept ────────────────────── */
  if (!topic) return shell(<><Crumbs items={[{ label: 'Knowledge Hub', onClick: goHome }]} /><div className="hb-empty">That skill wasn't found.</div></>);
  const tq = topicQuery.trim().toLowerCase();
  const navSubs = subs.filter((s) => !tq || s.name.toLowerCase().includes(tq)
    || (s.children || []).some((c) => c.name.toLowerCase().includes(tq)));
  const skillSt = mem[topic.id];
  const skillNav = (
    <nav className="kh-nav" aria-label={`${topic.name} topics`}>
      <label className="kh-search">
        <IconSearch size={14} />
        <input type="search" value={topicQuery} onChange={(e) => setTopicQuery(e.target.value)}
          placeholder="Search topics…" aria-label={`Search ${topic.name} topics`} />
      </label>
      <ol>
        {navSubs.map((s, i) => {
          const kids = (s.children || []).length ? s.children : [];
          const open = expanded === s.id || (tq && kids.some((c) => c.name.toLowerCase().includes(tq)));
          return (
            <li key={s.id}>
              <button type="button" className={`kh-nav-topic${sub?.id === s.id ? ' on' : ''}`}
                aria-expanded={kids.length ? !!open : undefined}
                onClick={() => { setExpanded(open && sub?.id === s.id ? null : s.id); pick({ sub: s.id }); }}>
                <span className="kh-nav-num">{i + 1}</span>
                <span className="kh-nav-name">{s.name}</span>
                <span className="kh-nav-count">{kids.length || ''}</span>
              </button>
              {open && kids.length > 0 && (
                <ul>
                  {kids.filter((c) => !tq || c.name.toLowerCase().includes(tq) || s.name.toLowerCase().includes(tq)).map((c) => (
                    <li key={c.id}>
                      <button type="button" className={c.id === openNodeId ? 'on' : ''} onClick={() => onOpenNode?.(c.id)}>
                        <IconChevRight size={11} /> {c.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );

  let main;
  if (located) {
    main = (
      <HubConceptPage
        concept={located.concept} topic={located.topic} sub={located.sub}
        mem={mem} siblings={located.siblings} isUser={false} myNodes={myNodes}
        crumbs={[
          { label: 'Knowledge Hub', onClick: goHome },
          { label: topic.name, onClick: () => pick({ sub: undefined }) },
          ...(located.sub && located.sub.id !== located.concept.id ? [{ label: located.sub.name, onClick: () => pick({ sub: located.sub.id }) }] : []),
          { label: located.concept.name },
        ]}
        onEdit={openNotesEditor} onPracticeWith={practiceWith} onNewPage={() => newPage(null)}
        onNavigate={({ concept: c }) => { if (c) onOpenNode?.(c); }} />
    );
  } else if (topic.id === 'dsa') {
    main = (
      <div className="kh-topic">
        <p className="hb-empty">Coding practice lives in its own workspace — a real editor with run, submit and an AI assistant on every problem.</p>
        <button className="pu-btn pu-btn-primary" type="button" onClick={onOpenDsa}><IconCode size={14} /> Open DSA Problems</button>
      </div>
    );
  } else if (sub) {
    main = (
      <div className="kh-topic">
        <Crumbs items={[{ label: 'Knowledge Hub', onClick: goHome }, { label: topic.name, onClick: () => pick({ sub: undefined }) }, { label: sub.name }]} />
        <header className="kh-topic-head">
          <div>
            <h2>{sub.name}</h2>
            <p className="pu-meta">{concepts.length} concept{concepts.length === 1 ? '' : 's'} · {revisionLine(mem[sub.id])}</p>
          </div>
          <button type="button" className="pu-btn pu-btn-primary" onClick={() => practiceWith([{ id: sub.id, name: sub.name, kind: 'topic' }], sub.name)}>
            <IconPlus size={14} /> Practice this
          </button>
        </header>
        <div className="kh-list">
          {concepts.map((c, i) => {
            const st = mem[c.id];
            const sm = summaries[c.id];
            const imp = IMPORTANCE[(c.meta || {}).importance];
            const line = sm?.intro
              || (sm?.question_count ? `${sm.question_count} interview question${sm.question_count > 1 ? 's' : ''} inside — theory notes coming.` : '');
            return (
              <button key={c.id} type="button" className="kh-row" onClick={() => onOpenNode?.(c.id)}>
                <span className="kh-row-num">{String(i + 1).padStart(2, '0')}</span>
                <span className="kh-row-main">
                  <b>{c.name}</b>
                  {sm === undefined ? <span className="hb-skel"><i /></span> : <small>{line || 'No material yet.'}</small>}
                </span>
                <span className="kh-row-side">
                  {imp && <span className="kh-source">{imp[0]}</span>}
                  <Mastery value={st?.mastery} />
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  } else {
    main = (
      <div className="kh-topic">
        <p className="kh-skill-intro">{blurb(topic) || `Everything in ${topic.name}, organised by topic.`}</p>
        <div className="kh-list">
          {subs.map((s, i) => {
            const st = mem[s.id];
            const kids = (s.children || []).length;
            return (
              <button key={s.id} type="button" className="kh-row" onClick={() => pick({ sub: s.id })}>
                <span className="kh-row-num">{String(i + 1).padStart(2, '0')}</span>
                <span className="kh-row-main">
                  <b>{s.name}</b>
                  <small>{kids ? `${kids} concept${kids === 1 ? '' : 's'}` : 'Open topic'} · {revisionLine(st)}</small>
                </span>
                <span className="kh-row-side"><Mastery value={st?.mastery} /></span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return shell(
    <>
      <section className="kh-skill-head">
        {!located && !sub && <Crumbs items={[{ label: 'Knowledge Hub', onClick: goHome }, { label: topic.name }]} />}
        {!located && !sub && (
          <header className="kh-concept-head">
            <div className="kh-concept-title">
              <h1>{topic.name}</h1>
            </div>
            <div className="kh-actions">
              <button type="button" className="pu-btn pu-btn-quiet" onClick={() => newPage(null)}><IconPlus size={14} /> Add page</button>
              <button type="button" className="pu-btn pu-btn-primary" onClick={() => practiceWith([{ id: topic.id, name: topic.name, kind: 'topic' }], topic.name)}>
                Practice this
              </button>
            </div>
          </header>
        )}
        {!located && !sub && (
          <p className="kh-meta">
            <span>{subs.length} topics</span>
            <span>{revisionLine(skillSt)}</span>
            {skillSt?.mastery != null && <Mastery value={skillSt.mastery} />}
          </p>
        )}
      </section>
      <div className="kh-split">
        {skillNav}
        <div className="kh-split-main">{main}</div>
      </div>
    </>,
  );
};

/* ── My Hub: topics → subtopics → pages ─────────────────────────────────── */
const MyHubPanel = ({ myNodes, myTopics, mySubs, myPages, sel, onSelect, mem, onOpen,
  onNewPage, onDraftWithAi, onCreateTopic, onRenameTopic, onDeleteTopic, prompt, toast }) => {
  const [addingTopic, setAddingTopic] = useState(false);
  const [topicName, setTopicName] = useState('');
  const inputRef = useRef(null);

  const subsOf = (topicId) => mySubs.filter((s) => s.parent_id === topicId);
  const pagesUnder = (id) => {
    const subIds = subsOf(id).map((s) => s.id);
    return myPages.filter((p) => p.parent_id === id || subIds.includes(p.parent_id));
  };
  const unfiled = myPages.filter((p) => !p.parent_id);
  const selNode = myTopics.find((t) => t.id === sel) || mySubs.find((s) => s.id === sel);
  const pages = sel === 'all' ? myPages
    : sel === 'unfiled' ? unfiled
      : selNode?.kind === 'topic' ? pagesUnder(sel)
        : myPages.filter((p) => p.parent_id === sel);

  const addTopic = async () => {
    const name = topicName.trim();
    if (!name) return;
    try {
      const node = await onCreateTopic(name, 'topic', null);
      setTopicName(''); setAddingTopic(false);
      if (node?.id) onSelect(node.id);
    } catch (e) { toast.error(e.message); }
  };

  const addSubtopic = async (topic) => {
    const name = await prompt({ title: `New subtopic in "${topic.name}"`, placeholder: 'Subtopic name',
      confirmLabel: 'Create', validate: (v) => (v.trim() ? '' : 'Give it a name') });
    if (name == null) return;
    try {
      const node = await onCreateTopic(name, 'subtopic', topic.id);
      if (node?.id) onSelect(node.id);
    } catch (e) { toast.error(e.message); }
  };

  const folderRow = (n, depth) => (
    <div key={n.id} className={`hb-nav uh-folder${sel === n.id ? ' on' : ''}`}
      style={depth ? { paddingLeft: 30 } : undefined}>
      <button type="button" className="nm uh-folder-name" onClick={() => onSelect(n.id)}>{n.name}</button>
      <span className="n">{n.kind === 'topic' ? pagesUnder(n.id).length : myPages.filter((p) => p.parent_id === n.id).length}</span>
      <span className="uh-folder-acts">
        {n.kind === 'topic' && <button type="button" title="Add a subtopic" onClick={() => addSubtopic(n)}>+</button>}
        <button type="button" title="Rename" onClick={() => onRenameTopic(n)}>✎</button>
        <button type="button" title={n.kind === 'topic' ? 'Delete topic (pages move to Unfiled)' : 'Delete subtopic (pages move to the topic)'}
          onClick={() => onDeleteTopic(n)}>×</button>
      </span>
    </div>
  );

  return (
    <div className="kh-plain">
      <div className="kh-mypages-bar">
        <p className="pu-meta kh-tab-note">Your own pages and copies of shared ones. They practise like any other content.</p>
        <div className="hb-hero-side">
          <button className="hb-btn go" type="button"
            onClick={() => onNewPage(sel !== 'all' && sel !== 'unfiled' ? sel : null)}>+ New page</button>
          {addingTopic ? (
            <span className="uh-newtopic">
              <input ref={inputRef} autoFocus value={topicName} maxLength={60} placeholder="Topic name"
                onChange={(e) => setTopicName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') addTopic(); if (e.key === 'Escape') setAddingTopic(false); }} />
              <button className="hb-btn" type="button" onClick={addTopic} disabled={!topicName.trim()}>Add</button>
            </span>
          ) : (
            <button className="hb-btn" type="button" onClick={() => setAddingTopic(true)}>+ New topic</button>
          )}
          {onDraftWithAi && <button className="hb-btn" type="button" onClick={onDraftWithAi}>Draft with AI / import</button>}
        </div>
      </div>

      {(myTopics.length > 0 || myPages.length > 0) && (
        <div className="uh-folders">
          <button type="button" className={`hb-nav${sel === 'all' ? ' on' : ''}`} onClick={() => onSelect('all')}>
            <span className="nm">All pages</span><span className="n">{myPages.length}</span>
          </button>
          {myTopics.map((t) => (
            <React.Fragment key={t.id}>
              {folderRow(t, 0)}
              {subsOf(t.id).map((s) => folderRow(s, 1))}
            </React.Fragment>
          ))}
          {myTopics.length > 0 && unfiled.length > 0 && (
            <button type="button" className={`hb-nav${sel === 'unfiled' ? ' on' : ''}`} onClick={() => onSelect('unfiled')}>
              <span className="nm">Unfiled</span><span className="n">{unfiled.length}</span>
            </button>
          )}
        </div>
      )}

      {myNodes === null ? <div className="hb-skel"><i /><i /><i /></div>
        : pages.length === 0 ? (
          <div className="hb-empty">
            {myPages.length === 0
              ? 'Nothing here yet — open any concept and "Edit my copy", or start a page from scratch.'
              : 'No pages here yet — file one from its editor.'}
          </div>
        ) : (
          <div className="hb-index">
            {pages.map((n, i) => {
              const st = mem[n.concept_id];
              return (
                <button key={n.id} type="button" className="hb-row" onClick={() => onOpen(n.concept_id)}>
                  <span className="hb-row-num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="hb-row-main">
                    <h3>{n.name}</h3>
                    <p>{n.intro || `${n.question_count} question${n.question_count === 1 ? '' : 's'}`}</p>
                  </span>
                  <span className="hb-row-side">
                    <span className="kh-source">{n.forked_from ? 'Copy of a shared page' : 'My notes'}</span>
                    <Mastery value={st?.mastery} />
                    {st?.attempts > 0 && <span className="cnt">{revisionLine(st)}</span>}
                  </span>
                </button>
              );
            })}
          </div>
        )}
    </div>
  );
};

export default KnowledgeHubV2;
